import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IExperiment, formatDate } from '../../types/experiment';
import styles from './ExperimentsList.module.css';

interface ExperimentsListProps {
    experiments: IExperiment[];
    loading?: boolean;
    emptyMessage?: string;
    className?: string;
}

/**
 * ExperimentsList - Table-based list view for displaying experiments
 * Matches the visual style of BondsList
 */
const ExperimentsList: React.FC<ExperimentsListProps> = ({
    experiments,
    loading = false,
    emptyMessage,
    className = ''
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const getStatusText = (experiment: IExperiment): string => {
        const rawStatus = experiment.progress?.run_status || experiment.experiment_data?.status || 'draft';
        const status = rawStatus.toLowerCase();
        return t(`experiments.status.${status}`) || status;
    };

    const getStatusClass = (experiment: IExperiment): string => {
        const rawStatus = experiment.progress?.run_status || experiment.experiment_data?.status || 'draft';
        return rawStatus.toLowerCase();
    };

    if (loading) {
        return (
            <div className={styles.loaderContainer}>
                <span className="material-icons rotating">sync</span>
                <p className={styles.loadingText}>
                    {t('loading.experiments')}
                </p>
            </div>
        );
    }

    if (!experiments || experiments.length === 0) {
        return (
            <div className={styles.emptyState}>
                <span className="material-icons" style={{ fontSize: '3rem', opacity: 0.5 }}>science</span>
                <h3 className={styles.emptyTitle}>
                    {t('experiments.noData', 'No experiments found')}
                </h3>
                <p className={styles.emptyMessage}>
                    {emptyMessage || t('experiments.noDataFiltered', 'Try adjusting your filters or search criteria.')}
                </p>
            </div>
        );
    }

    const handleRowClick = (experimentId: string) => {
        navigate(`/experiments/${experimentId}`);
    };

    return (
        <div className={`${styles.container} ${className}`}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th className={styles.headerStatus}>{t('experiments.filters.status', 'STATUS')}</th>
                        <th className={styles.headerName}>{t('experiments.experimentId', 'EXPERIMENT ID')}</th>
                        <th className={styles.headerType}>{t('experiments.experimentType', 'TYPE')}</th>
                        <th className={styles.headerProvider}>{t('experiments.provider', 'PROVIDER')}</th>
                        <th className={styles.headerDocuments}>{t('experiments.documents', 'DOCUMENTS')}</th>
                        <th className={styles.headerCreated}>{t('experiments.created', 'CREATED')}</th>
                        <th className={styles.headerUpdated}>{t('experiments.updated', 'UPDATED')}</th>
                        <th className={styles.headerLastRun}>{t('experiments.lastRun', 'LAST RUN')}</th>
                    </tr>
                </thead>
                <tbody>
                    {experiments.map((experiment) => {
                        const expData = experiment.experiment_data;
                        return (
                            <tr
                                key={experiment._id}
                                className={styles.clickableRow}
                                onClick={() => handleRowClick(experiment._id)}
                            >
                                <td className={styles.statusCell}>
                                    <span className={`${styles.statusBadge} ${styles[getStatusClass(experiment)]}`}>
                                        {getStatusText(experiment)}
                                    </span>
                                </td>
                                <td className={styles.nameCell}>
                                    {experiment.experiment_id || t('experiments.unnamed')}
                                </td>
                                <td className={styles.typeCell}>
                                    {expData?.type || '—'}
                                </td>
                                <td className={styles.providerCell}>
                                    {expData?.provider && expData?.model
                                        ? `${expData.provider} / ${expData.model}`
                                        : expData?.provider || expData?.model || '—'}
                                </td>
                                <td className={styles.documentsCell}>
                                    {expData?.number_of_processed_documents || 0}
                                </td>
                                <td className={styles.dateCell}>
                                    {formatDate(experiment.created_timestamp || experiment.experiment_created_time)}
                                </td>
                                <td className={styles.dateCell}>
                                    {formatDate(experiment.last_used_timestamp)}
                                </td>
                                <td className={styles.dateCell}>
                                    {formatDate(experiment.progress?.finished_at || experiment.progress?.started_at)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ExperimentsList;
